import { Observable } from 'rxjs';
import { EventEmitter, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { FormAssignmentService } from '../../global-services/form-assignment.service';
import { FormService } from '../../global-services/form.service';
import { PatientService } from '../../global-services/patient.service';
import {
  CreatePatientFormAssignmentModel,
  UpdatePatientFormAssignmentModel,
  PatientFormAssignmentModel,
  PatientFormAssignmentStatus,
} from '../../global-data-models/form-assignment.model';
import { FormModel } from '../../global-data-models/form.model';
import { PatientModel } from '../../global-data-models/patient.model';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { v4 as uuidv4 } from 'uuid';
import { Router } from '@angular/router';

export enum CrudFormAssignmentMode {
  CREATE,
  READ,
  UPDATE,
}
export const DEFAULT_CRUD_FORM_ASSIGNMENT_MODE = CrudFormAssignmentMode.CREATE;

export interface CrudFormAssignmentUiState {
  form: FormGroup;
  crudMode: CrudFormAssignmentMode;
  availableForms: FormModel[];
  patientInfo?: PatientModel;
  formAssignment?: PatientFormAssignmentModel;
  formFillUrls?: {
    qr: string;
    link: string;
    email: string;
  };
}

@Injectable({ providedIn: 'root' })
export class CrudFormAssignmentFacade {
  public uiState: CrudFormAssignmentUiState;

  private uiStateEvent: EventEmitter<CrudFormAssignmentUiState> = new EventEmitter<CrudFormAssignmentUiState>();
  public uiState$: Observable<CrudFormAssignmentUiState> = this.uiStateEvent.asObservable();

  private orgId?: string;
  private patientId?: string;
  private userId?: string;

  constructor(
    private readonly loadingService: GlobalLoadingService,
    private readonly fb: FormBuilder,
    private readonly formAssignmentService: FormAssignmentService,
    private readonly formService: FormService,
    private readonly patientService: PatientService,
    private readonly messageService: GlobalMessageService,
    private readonly router: Router,
  ) {
    // Assign a temporary blank ui state with CREATE mode form
    this.uiState = {
      form: this.createInitialForm(),
      crudMode: DEFAULT_CRUD_FORM_ASSIGNMENT_MODE,
      availableForms: [],
      patientInfo: undefined,
      formFillUrls: undefined,
    };

    this.uiStateEvent.emit(this.uiState);
  }

  private createInitialForm(): FormGroup {
    // Create an initial form with all possible controls to prevent template errors
    const form = this.fb.group({
      id: [{ value: uuidv4(), disabled: true }],
      formId: ['', Validators.required],
      patientId: [{ value: '', disabled: true }],
      assignedBy: [{ value: '', disabled: true }],
      durationSeconds: [86400, [Validators.required, Validators.min(3600)]],
      durationHours: [24, [Validators.required]],
      expiresAt: [null],
      status: [null],
      fillingMethod: [null],
      progress: [null],
      filledByUserId: [null],
      assignedAt: [null],
      firstAccessedAt: [null],
      completedAt: [null],
      responseData: [null],
    });

    // Set up the relationship between durationHours and durationSeconds
    form.get('durationHours')?.valueChanges.subscribe((hours) => {
      // Allow empty values and 0 for user input flexibility
      if (typeof hours === 'number') {
        const seconds = hours > 0 ? hours * 3600 : 0;
        form.get('durationSeconds')?.setValue(seconds, { emitEvent: false });
      } else if (hours === null || hours === undefined || hours === '') {
        // Handle empty/cleared values
        form.get('durationSeconds')?.setValue(null, { emitEvent: false });
      }
    });

    return form;
  }

  public async begin(_orgId: string, _patientId: string, _userId: string, _crudMode: CrudFormAssignmentMode, assignmentId?: string) {
    try {
      this.loadingService.show();

      this.orgId = _orgId;
      this.patientId = _patientId;
      this.userId = _userId;

      let assignmentData: PatientFormAssignmentModel | undefined = undefined;
      let formFillUrls: CrudFormAssignmentUiState['formFillUrls'] = undefined;

      if (_crudMode === CrudFormAssignmentMode.READ || _crudMode === CrudFormAssignmentMode.UPDATE) {
        if (!assignmentId) {
          console.log('Tried to read|update but assignmentId undefined.');
          return;
        }

        assignmentData = await this.formAssignmentService.getPatientFormAssignment(this.orgId!, this.patientId!, assignmentId);

        if (_crudMode === CrudFormAssignmentMode.READ && assignmentId && assignmentData.accessToken) {
          const baseUrl = `${window.location.origin}/form-fill/${assignmentId}`;
          formFillUrls = {
            qr: `${baseUrl}?token=${assignmentData.accessToken}&method=qr-self`,
            link: `${baseUrl}?token=${assignmentData.accessToken}&method=url-subject`,
            email: `mailto:?subject=Form Assignment&body=Please fill out this form: ${baseUrl}?token=${assignmentData.accessToken}&method=email-subject`,
          };
        }
      }

      // Fetch available forms for the organization
      const formsPage = await this.formService.getFormsPage({
        orgId: this.orgId!,
        page: 1,
        orderDir: 'asc' as any,
        limit: 10000, // Get enough forms for dropdown
        isActive: true,
        status: 'PUBLISHED',
      });

      // Fetch patient information
      const patientInfo = await this.patientService.getPatient(this.orgId!, this.patientId!);

      this.uiState = {
        form: this.createForm(assignmentData, _crudMode),
        crudMode: _crudMode,
        availableForms: formsPage.data,
        patientInfo: patientInfo,
        formAssignment: assignmentData,
        formFillUrls: formFillUrls,
      };

      this.uiStateEvent.emit(this.uiState);
    } catch (error: any) {
      this.messageService.showError('Error', error.message);
      throw Error(error);
    } finally {
      this.loadingService.hide();
    }
  }

  private createForm(assignment: PatientFormAssignmentModel | undefined, mode: CrudFormAssignmentMode): FormGroup {
    const isReadMode = mode === CrudFormAssignmentMode.READ;

    if (mode === CrudFormAssignmentMode.CREATE) {
      const form = this.fb.group({
        id: [{ value: uuidv4(), disabled: true }],
        formId: ['', Validators.required],
        patientId: [{ value: this.patientId, disabled: true }],
        assignedBy: [{ value: this.userId, disabled: true }],
        durationSeconds: [86400, [Validators.required, Validators.min(3600)]],
        durationHours: [24, [Validators.required]],
      });

      // Set up the relationship between durationHours and durationSeconds
      form.get('durationHours')?.valueChanges.subscribe((hours) => {
        if (typeof hours === 'number') {
          const seconds = hours > 0 ? hours * 3600 : 0;
          form.get('durationSeconds')?.setValue(seconds, { emitEvent: false });
        } else if (hours === null || hours === undefined || hours === '') {
          form.get('durationSeconds')?.setValue(null, { emitEvent: false });
        }
      });
      return form;
    }

    // For READ and UPDATE modes
    const form = this.fb.group({
      id: [{ value: assignment?.id, disabled: true }],
      formId: [{ value: assignment?.formId, disabled: isReadMode }, Validators.required],
      patientId: [{ value: assignment?.patientId, disabled: true }],
      assignedBy: [{ value: assignment?.assignedBy, disabled: true }],
      expiresAt: [{ value: assignment?.expiresAt, disabled: isReadMode }],
      status: [{ value: assignment?.status, disabled: isReadMode }],
      fillingMethod: [{ value: assignment?.fillingMethod, disabled: isReadMode }],
      progress: [{ value: assignment?.progress, disabled: isReadMode }],
      filledByUserId: [{ value: assignment?.filledByUserId, disabled: isReadMode }],
      assignedAt: [{ value: assignment?.assignedAt, disabled: true }],
      firstAccessedAt: [{ value: assignment?.firstAccessedAt, disabled: true }],
      completedAt: [{ value: assignment?.completedAt, disabled: true }],
      durationSeconds: [0],
      durationHours: [0],
      responseData: [assignment?.responseData],
    });

    if (isReadMode) {
      form.disable();
    }

    return form;
  }

  public resetForm(): void {
    this.uiState.form.reset();
  }

  public async submit() {
    // Validate duration hours before submission
    const durationHours = this.uiState.form.get('durationHours')?.value;
    if (this.uiState.crudMode === CrudFormAssignmentMode.CREATE && (!durationHours || durationHours <= 0)) {
      this.messageService.showError('Validation Error', 'Duration must be at least 1 hour');
      return;
    }

    const assignmentModel = this.formToAssignmentModel();

    try {
      if (this.uiState.crudMode === CrudFormAssignmentMode.CREATE) {
        const createdAssignemnt = await this.formAssignmentService.createPatientFormAssignment(this.orgId!, this.patientId!, assignmentModel as CreatePatientFormAssignmentModel);
        // navigate to orgs/:orgId/patients/:patientId/form-assignments/:assignmentId/view
        this.messageService.showSuccess('Success', 'Form assignment created successfully');
        this.router.navigate(['orgs', this.orgId, 'patients', this.patientId, 'form-assignments', createdAssignemnt.id, 'view']);
        return;
      }

      if (this.uiState.crudMode === CrudFormAssignmentMode.UPDATE) {
        await this.formAssignmentService.updatePatientFormAssignment(
          this.orgId!,
          this.patientId!,
          this.uiState.form.get('id')?.getRawValue(),
          assignmentModel as UpdatePatientFormAssignmentModel,
        );
      }

      this.messageService.showSuccess('Success', 'Form assignment saved successfully');
    } catch (error: any) {
      this.messageService.showError('Error', 'Failed to save form assignment: ' + error.message || error);
      throw new Error(error);
    }
  }

  private formToAssignmentModel(): CreatePatientFormAssignmentModel | UpdatePatientFormAssignmentModel {
    const formValues = this.uiState.form.getRawValue();

    const assignmentModel: Record<string, any> = {
      formId: formValues.formId,
      patientId: this.patientId!,
      assignedBy: this.userId!,
    };

    if (this.uiState.crudMode === CrudFormAssignmentMode.CREATE) {
      assignmentModel['durationSeconds'] = formValues.durationSeconds;
      // Don't include status, fillingMethod, or filledByUserId in CREATE mode
    } else {
      // Include all fields for UPDATE mode
      assignmentModel['status'] = formValues.status;
      assignmentModel['fillingMethod'] = formValues.fillingMethod;
      assignmentModel['filledByUserId'] = formValues.filledByUserId;
      assignmentModel['expiresAt'] = formValues.expiresAt;
      assignmentModel['responseData'] = formValues.responseData;
      assignmentModel['progress'] = formValues.progress;
      assignmentModel['assignedAt'] = formValues.assignedAt;
      assignmentModel['firstAccessedAt'] = formValues.firstAccessedAt;
      assignmentModel['completedAt'] = formValues.completedAt;
    }

    if (this.uiState.crudMode === CrudFormAssignmentMode.UPDATE) {
      assignmentModel['id'] = formValues.id;
    }

    return assignmentModel;
  }

  /**
   * Placeholder method for extending form expiry
   * TODO: Implement backend API call for extending form expiry
   */
  public async extendFormExpiry(duration: '1_HOUR' | '1_DAY' | '7_DAYS' | '28_DAYS'): Promise<void> {
    try {
      this.loadingService.show();

      // TODO: Implement actual API call to extend form expiry
      // const updatedAssignment = await this.formAssignmentService.extendFormExpiry(
      //   this.orgId!,
      //   this.patientId!,
      //   this.uiState.formAssignment!.id,
      //   duration
      // );

      // For now, just log the action
      console.log(`Extending form expiry by: ${duration}`);

      // TODO: Update the UI state with the new expiry date
      // this.uiState.formAssignment = updatedAssignment;
      // this.uiStateEvent.emit(this.uiState);

      this.messageService.showSuccess('Success', `Form expiry extended by ${duration.replace('_', ' ').toLowerCase()}`);
    } catch (error: any) {
      this.messageService.showError('Error', 'Failed to extend form expiry: ' + error.message);
      throw error;
    } finally {
      this.loadingService.hide();
    }
  }
}
